import { put, call, takeLatest, fork, delay, select } from 'redux-saga/effects'
import axios from 'axios'

import RootState from '../root-state'
import { foodsActionType, foodsActions } from './actions'
import { auth0Actions } from '../auth0'
import { toastActions } from '../toast'
import { modalActions } from '../modal'
import {
  getFoods,
  getFood,
  changeFood,
  presignFoodImageUrl,
  newFood,
  updateFoodsIndex,
} from '../api'

export function* fetchFoods(
  action: ReturnType<typeof foodsActions.fetchFoods>
) {
  try {
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const { brandCode } = action.payload
    const response = yield call(getFoods, token, brandCode)
    yield put(foodsActions.fetchFoodsSucceeded(response.foods))
  } catch (error) {
    yield put(foodsActions.throwError(error))
  }
}

export function* fetchFood(action: ReturnType<typeof foodsActions.fetchFood>) {
  try {
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const { foodId } = action.payload
    const response = yield call(getFood, token, foodId)
    yield put(foodsActions.fetchFoodSucceeded(response.food))
  } catch (error) {
    yield put(foodsActions.throwError(error))
  }
}

export function* fetchFoodsEvery() {
  try {
    while (true) {
      const state: RootState = yield select()
      const active = state.foods.active
      if (active !== 'foods') {
        return
      }
      const currentBrandCode = state.foods.currentBrandCode
      yield put(foodsActions.fetchFoods(currentBrandCode))
      yield delay(5000)
    }
  } catch (error) {
    yield put(foodsActions.throwError(error))
  }
}

export function* uploadFoodImage(
  action: ReturnType<typeof foodsActions.uploadFoodImage>
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const presignResponse = yield call(
      presignFoodImageUrl,
      token,
      action.payload.foodId,
      action.payload.foodImageType
    )
    const { fileName, url } = presignResponse.result
    yield call(axios.put, url, action.payload.file, {
      headers: {
        'Content-Type': action.payload.file.type,
      },
    })
    yield put(
      foodsActions.setImageUrl(fileName, url, action.payload.foodImageType)
    )
    yield delay(100)
  } catch (error) {
    yield put(foodsActions.throwError(error))
    yield put(
      toastActions.show({
        type: 'error',
        title: '失敗...',
        body: '画像のアップロードに失敗しました',
      })
    )
  }
}

export function* setFood(action: ReturnType<typeof foodsActions.setFood>) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    yield call(changeFood, token, action.payload.foodId, action.payload.food)
    yield delay(100)
    const state2: RootState = yield select()
    const currentBrandCode = state2.foods.currentBrandCode
    yield put(foodsActions.fetchFood(action.payload.foodId))
    yield put(foodsActions.fetchFoods(currentBrandCode))
    yield put(
      toastActions.show({ title: '成功！', body: 'フードの情報を更新しました' })
    )
    yield put(modalActions.hide())
  } catch (error) {
    yield put(foodsActions.throwError(error))
    yield put(
      toastActions.show({
        type: 'error',
        title: '失敗...',
        body: '情報の更新に失敗しました',
      })
    )
  }
}

export function* createFood(
  action: ReturnType<typeof foodsActions.createFood>
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const { food, brandCode } = action.payload

    yield call(newFood, token, food, brandCode)
    yield put(foodsActions.createFoodSucceeded())
    yield put(
      toastActions.show({ title: '成功！', body: 'フードの情報を保存しました' })
    )
  } catch (error) {
    yield put(foodsActions.throwError(error))
    yield put(
      toastActions.show({
        type: 'error',
        title: '失敗...',
        body: 'フードの追加に失敗しました',
      })
    )
  }
}

export function* changeFoodsIndex(
  action: ReturnType<typeof foodsActions.changeFoodsIndex>
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    yield call(updateFoodsIndex, token, action.payload.ids)
    yield delay(100)
    // APIコール後に最新の状態を取得
    const updatedState: RootState = yield select()
    const currentBrandCode = updatedState.foods.currentBrandCode
    yield put(foodsActions.fetchFoods(currentBrandCode))
  } catch (error) {
    yield put(foodsActions.throwError(error))
  }
}

export function* watchFetchFoods() {
  yield takeLatest(foodsActionType.FETCH_FOODS, fetchFoods)
}

export function* watchFetchFood() {
  yield takeLatest(foodsActionType.FETCH_FOOD, fetchFood)
}

export function* watchFetchFoodsEvery() {
  yield takeLatest(foodsActionType.FETCH_FOODS_EVERY, fetchFoodsEvery)
}

export function* watchSetFood() {
  yield takeLatest(foodsActionType.SET_FOOD, setFood)
}

export function* watchUploadFoodImage() {
  yield takeLatest(foodsActionType.UPLOAD_FOOD_IMAGE, uploadFoodImage)
}

export function* watchCreateFood() {
  yield takeLatest(foodsActionType.CREATE_FOOD, createFood)
}

export function* watchChangeFoodsIndex() {
  yield takeLatest(foodsActionType.CHANGE_FOODS_INDEX, changeFoodsIndex)
}

export const foodsSagas = [
  fork(watchFetchFoods),
  fork(watchFetchFood),
  fork(watchFetchFoodsEvery),
  fork(watchSetFood),
  fork(watchUploadFoodImage),
  fork(watchCreateFood),
  fork(watchChangeFoodsIndex),
]
